import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { FaTimes, FaFolderOpen, FaDownload, FaTrash, FaFile, FaFileImage, FaFilePdf, FaFileCode, FaPaperPlane } from 'react-icons/fa';
import { ref, uploadBytes, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getMockupUrl, getPersistableMockupUrl } from '../utils/fileHelpers';
import { upsertCustomerDesignMockup } from '../utils/designMockupIndex';
import { buildLineSnapshotFromLine, applyLibraryMockupToLine } from '../utils/customerDesignLibrary';
import CustomerMockupsModal from './CustomerMockupsModal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-surface);
  padding: 16px;
  border-radius: 4px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  gap: 12px;
  flex: 1;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 18px;
  z-index: 10;
  padding: 4px;
  
  &:hover {
    color: var(--al-primary);
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  h3 {
    margin: 0 0 2px 0;
    font-size: 1.2em;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.75em;
    text-transform: none;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 0.9em;
  font-weight: 600;
  color: var(--al-primary);
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.uploading ? 'var(--al-primary)' : props.hasPreview ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 4px;
  padding: ${props => props.hasPreview ? '8px' : '12px'};
  text-align: center;
  background: ${props => props.uploading ? 'rgba(var(--al-primary-rgb), 0.1)' : props.hasPreview ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
  margin-bottom: ${props => props.hasList ? '12px' : '0'};
  transition: all 0.2s;
  cursor: ${props => props.uploading ? 'default' : 'pointer'};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100px;
  position: relative;
  
  &:hover {
    border-color: ${props => props.uploading ? 'var(--al-primary)' : props.hasPreview ? 'rgba(255, 255, 255, 0.2)' : 'rgba(var(--al-primary-rgb), 0.5)'};
    background: ${props => props.uploading ? 'rgba(var(--al-primary-rgb), 0.1)' : props.hasPreview ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.3)'};
  }
  
  p {
    margin: 0 0 6px 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.75em;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    display: block;
    margin: 0 auto;
    flex: 1;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(211, 47, 47, 0.9);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const UploadButton = styled.button`
  background: var(--al-primary);
  color: #222;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.75em;
  font-weight: 600;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: #7FFF7F;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LibraryButton = styled.button`
  background: transparent;
  color: var(--al-primary);
  border: 1px solid var(--al-primary);
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.75em;
  font-weight: 600;
  font-family: inherit;
  text-transform: uppercase;
  margin-top: 8px;

  &:hover {
    background: rgba(var(--al-primary-rgb), 0.12);
  }
`;

const FilesList = styled.div`
  overflow-y: auto;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
  padding: 4px;
  min-height: 0;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FileIcon = styled.div`
  color: var(--al-primary);
  font-size: 1em;
  flex-shrink: 0;
`;

const FileNameContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FileName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.9);
  text-transform: none;
`;

const FileTypeSelector = styled.select`
  padding: 4px 6px;
  background: #363636;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.7em;
  text-transform: none;
  cursor: pointer;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: var(--al-primary);
  }
  
  &:hover {
    border-color: rgba(var(--al-primary-rgb), 0.5);
  }
`;

const FileActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const FileActionButton = styled.button`
  background: ${props => props.danger ? '#d32f2f' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 0.75em;
  min-width: 28px;
  
  &:hover {
    background: ${props => props.danger ? '#c62828' : 'rgba(var(--al-primary-rgb), 0.3)'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;


const EmptyState = styled.p`
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: none;
  font-size: 0.75em;
  margin: 0;
`;

// Helper functions
function sanitizeFolderName(name) {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function getFileIcon(fileName) {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
      return <FaFileImage />;
    case 'pdf':
      return <FaFilePdf />;
    case 'dst':
    case 'emb':
    case 'pes':
    case 'exp':
      return <FaFileCode />;
    default:
      return <FaFile />;
  }
}

function isPreviewable(fileName) {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'pdf'].includes(extension);
}

function serializeProductionFilesForFirestore(files = []) {
  return (files || []).map((f) => ({
    name: f.name || '',
    url: f.url || '',
    type: f.type || 'unassigned',
    storagePath: f.storagePath || null,
    sentToPrintAt: f.sentToPrintAt || null,
    driveFileId: f.driveFileId || null,
    driveWebViewLink: f.driveWebViewLink || null,
    source: f.source || null
  }));
}

// File type options - combining location and purpose
const FILE_TYPE_OPTIONS = [
  { value: 'front-production', label: 'Front - Production' },
  { value: 'back-production', label: 'Back - Production' },
  { value: 'other-production', label: 'Other - Production' },
  { value: 'generic-production', label: 'Generic - Production' },
  { value: 'front-mockup', label: 'Front - Mockup' },
  { value: 'back-mockup', label: 'Back - Mockup' },
  { value: 'other-mockup', label: 'Other - Mockup' },
  { value: 'generic-mockup', label: 'Generic - Mockup' },
  { value: 'unassigned', label: 'Unassigned' }
];

const FilesModal = ({ 
  isOpen, 
  onClose, 
  orderNumber, 
  designName,
  lineId = null,  // Line document ID for updating Firestore
  lineData = null,  // For new order creation - line data
  onFilesUpdate = null,  // Callback when files are updated (for new orders)
  customerId = null,
  orderId = null
}) => {
  const [mockupFile, setMockupFile] = useState(null);
  const [mockupPreviewUrl, setMockupPreviewUrl] = useState(null);
  const [productionFiles, setProductionFiles] = useState([]);
  const [mockupUploading, setMockupUploading] = useState(false);
  const [productionUploading, setProductionUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const mockupInputRef = useRef(null);
  const productionInputRef = useRef(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [sendingToPrintKey, setSendingToPrintKey] = useState(null);
  const [showMockupLibrary, setShowMockupLibrary] = useState(false);

  // Prevent stale prop merges from wiping mockup/production file arrays when parent updates are async.
  const notifyParentWithFiles = ({ mockup, production, extra = {} } = {}) => {
    if (!onFilesUpdate || !lineData) return;
    const currentFiles = lineData.files || {};
    const mergedFiles = {
      ...currentFiles,
      mockup: mockup !== undefined ? mockup : currentFiles.mockup,
      production: production !== undefined ? production : (currentFiles.production || productionFiles)
    };
    onFilesUpdate({
      ...lineData,
      ...extra,
      files: mergedFiles
    });
  };

  // Load existing files when modal opens. Prefer Firestore when we have a saved line (lineId)
  // so we always show the latest saved mockup URL, not stale in-memory or blob URLs from lineData.
  useEffect(() => {
    if (isOpen && lineId) {
      loadExistingFiles();
    } else if (isOpen && lineData) {
      loadFilesFromLineData();
    }
  }, [isOpen, orderNumber, designName, lineData, lineId]);

  // Load files from existing order
  const loadExistingFiles = async () => {
    setLoading(true);
    try {
      const sanitized = sanitizeFolderName(designName);
      
      // First, check Firestore for files (SINGLE SOURCE OF TRUTH)
      let mockupUrl = null;
      let firestoreProductionFiles = [];
      let firestoreChecked = false;
      
      if (lineId) {
        try {
          const lineRef = doc(db, 'orderLines', lineId);
          const lineDoc = await getDoc(lineRef);
          const lineData = lineDoc.data();
          firestoreChecked = true;
          
          // Load mockup from Firestore (ignore blob URLs - they are temporary and break after refresh)
          const raw = lineData?.files?.mockup?.url || lineData?.designFileUrl || lineData?.mockupUrl;
          if (raw && !String(raw).startsWith('blob:')) {
            mockupUrl = raw;
            setMockupPreviewUrl(mockupUrl);
            console.log('✓ Loaded mockup URL from Firestore:', mockupUrl);
          } else {
            if (raw && String(raw).startsWith('blob:')) {
              console.warn('⚠️ Firestore had blob URL (invalid); not displaying');
            }
            console.log('✓ Firestore checked: No mockup (null or deleted)');
          }
          
          // Load production files from Firestore
          if (lineData?.files?.production && Array.isArray(lineData.files.production)) {
            firestoreProductionFiles = lineData.files.production;
            console.log('✓ Loaded production files from Firestore:', firestoreProductionFiles.length);
          }
        } catch (error) {
          console.error('Error loading from Firestore:', error);
          firestoreChecked = false;
        }
      }
      
      // ONLY check Storage if Firestore wasn't checked (for orders without lineId)
      // Don't check Storage if Firestore says "no mockup" - that's the source of truth!
      if (!mockupUrl && !firestoreChecked) {
        console.log('ℹ No Firestore lineId - checking Storage for legacy files...');
        const extensions = ['png', 'jpg', 'jpeg', 'gif', 'svg'];
        for (const ext of extensions) {
          try {
            const mockupRef = ref(storage, `artwork/${orderNumber}/${sanitized}/Mockups/mockup.${ext}`);
            mockupUrl = await getDownloadURL(mockupRef);
            setMockupPreviewUrl(mockupUrl);
            console.log('Loaded mockup URL from Storage:', mockupUrl);
            break; // Found it, stop looking
          } catch (e) {
            // Try next extension
          }
        }
        
        // If we found a legacy mockup in Storage, DON'T sync it to Firestore
        // (We're only here because Firestore doesn't have lineId, no need to sync)
        if (mockupUrl) {
          console.log('ℹ Found legacy mockup in Storage (not syncing - no lineId)');
        }
      }
      
      // If we have production files from Firestore, use those
      if (firestoreProductionFiles.length > 0) {
        setProductionFiles(firestoreProductionFiles);
      } else {
        // Fallback: Load production files from Storage (for legacy orders)
        const files = [];
        const possibleTypes = ['Front', 'Back', 'Other', 'Generic'];
        
        for (const type of possibleTypes) {
          try {
            const typeFolder = sanitizeFolderName(type);
            const folderRef = ref(storage, `artwork/${orderNumber}/${sanitized}/Production/${typeFolder}/`);
            const res = await listAll(folderRef);
            
            for (const itemRef of res.items) {
              const url = await getDownloadURL(itemRef);
              // Convert old type format to new format (e.g., 'Front' -> 'front-production')
              const newType = type.toLowerCase() + '-production';
              files.push({
                name: itemRef.name,
                url,
                type: newType,
                ref: itemRef
              });
            }
          } catch (e) {
            // Folder might not exist
          }
        }
        
        setProductionFiles(files);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load files from lineData (for new orders or when lineData is passed)
  const loadFilesFromLineData = () => {
    // Use getMockupUrl to check all possible mockup sources
    const mockupUrl = getMockupUrl(lineData);
    if (mockupUrl) {
      setMockupPreviewUrl(mockupUrl);
      console.log('✓ Loaded mockup URL from lineData:', mockupUrl);
    }
    
    // Load production files if available
    if (lineData?.files?.production) {
      setProductionFiles(lineData.files.production);
    }
  };

  // Handle mockup upload
  const handleMockupUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please upload an image file for mockup');
      return;
    }

    setMockupUploading(true);
    try {
      let fileUrl;
      const fileExtension = file.name.split('.').pop();
      
      // Determine the storage path based on available identifiers
      // Priority: lineId > orderNumber+designName > lineData.id
      const storageId = lineId || (orderNumber && designName ? `${orderNumber}/${sanitizeFolderName(designName)}` : lineData?.id);
      
      if (!storageId) {
        // No identifier available - create object URL for preview only
        // This will be uploaded when the order is saved
        fileUrl = URL.createObjectURL(file);
        console.log('⚠️ No storage ID - using preview URL. File will need to be uploaded on save.');
      } else {
        // Upload to Firebase Storage
        const storagePath = lineId 
          ? `artwork/lines/${lineId}/mockup.${fileExtension}`
          : `artwork/${orderNumber}/${sanitizeFolderName(designName)}/Mockups/mockup.${fileExtension}`;
        
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
        console.log('✅ Uploaded mockup to:', storagePath);
        
        // Update Firestore with the mockup URL
        if (lineId) {
          try {
            const lineRef = doc(db, 'orderLines', lineId);
            await updateDoc(lineRef, {
              'files.mockup': {
                url: fileUrl,
                fileName: file.name
              },
              designFileUrl: fileUrl, // Backward compatibility
              updatedAt: serverTimestamp()
            });
            console.log('✅ Updated Firestore with mockup URL');
          } catch (error) {
            console.error('Error updating Firestore with mockup URL:', error);
          }
        }
      }
      
      setMockupFile(file);
      setMockupPreviewUrl(fileUrl);
      
      // Notify parent component (ALWAYS - for both new and existing orders)
      if (onFilesUpdate && lineData) {
        notifyParentWithFiles({
          mockup: {
            url: fileUrl,
            fileName: file.name,
            ...(storageId ? {} : { file })
          },
          production: productionFiles,
          extra: {
            designFileUrl: fileUrl,
            ...(storageId ? {} : { _mockupFile: file })
          }
        });
      }

      const resolvedDesignName = designName || lineData?.design || lineData?.designName;
      if (customerId && resolvedDesignName && getPersistableMockupUrl(fileUrl)) {
        try {
          await upsertCustomerDesignMockup({
            customerId,
            designName: resolvedDesignName,
            mockupUrl: fileUrl,
            mockupFileName: file.name,
            sourceLineId: lineId || lineData?.docId || lineData?.id || null,
            sourceOrderId: orderId,
            sourceOrderNumber: orderNumber,
            lineSnapshot: buildLineSnapshotFromLine(lineData)
          });
        } catch (indexError) {
          console.error('Error updating design mockup index:', indexError);
        }
      }
    } catch (error) {
      console.error('Error uploading mockup:', error);
      alert('Failed to upload mockup: ' + error.message);
    } finally {
      setMockupUploading(false);
    }
  };

  // Handle production file upload
  const handleProductionUpload = async (files) => {
    if (files.length === 0) return;

    setProductionUploading(true);
    try {
      const newFiles = [];
      const defaultType = 'unassigned'; // Default type when file is first uploaded
      
      for (const file of files) {
        let fileUrl;
        
        if (lineId) {
          // Existing line - upload directly to line-scoped storage path
          const path = `artwork/lines/${lineId}/production/unassigned/${file.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, file);
          fileUrl = await getDownloadURL(storageRef);
          
          newFiles.push({
            name: file.name,
            url: fileUrl,
            type: defaultType,
            storagePath: path
          });
        } else if (lineData) {
          // New order - create object URL for preview
          fileUrl = URL.createObjectURL(file);
          // TODO: Calculate hash and check file library
          
          newFiles.push({
            name: file.name,
            url: fileUrl,
            type: defaultType,
            file: file
          });
        }
      }
      
      const updatedProductionFiles = [...productionFiles, ...newFiles];
      setProductionFiles(updatedProductionFiles);
      
      // Update Firestore for existing orders
      if (lineId) {
        try {
          const lineRef = doc(db, 'orderLines', lineId);
          const persistableFiles = serializeProductionFilesForFirestore(updatedProductionFiles);
          await updateDoc(lineRef, {
            'files.production': persistableFiles,
            updatedAt: serverTimestamp()
          });
          console.log('✅ Production files saved to Firestore');
        } catch (error) {
          console.error('❌ Error updating Firestore with production files:', error);
          // Don't fail the upload if Firestore update fails
        }
      }
      
      // Notify parent component for new orders
      if (onFilesUpdate && lineData) {
        notifyParentWithFiles({
          production: updatedProductionFiles
        });
      }
    } catch (error) {
      console.error('Error uploading production files:', error);
      alert('Failed to upload files: ' + error.message);
    } finally {
      setProductionUploading(false);
    }
  };

  // Handle file type change
  const handleFileTypeChange = async (fileIndex, newType) => {
    const updatedFiles = [...productionFiles];
    updatedFiles[fileIndex] = {
      ...updatedFiles[fileIndex],
      type: newType
    };
    setProductionFiles(updatedFiles);
    
    // Update Firestore for existing orders
    if (lineId) {
      try {
        const lineRef = doc(db, 'orderLines', lineId);
        const persistableFiles = serializeProductionFilesForFirestore(updatedFiles);
        await updateDoc(lineRef, {
          'files.production': persistableFiles,
          updatedAt: serverTimestamp()
        });
        console.log('✅ Production file type updated in Firestore');
      } catch (error) {
        console.error('❌ Error updating file type in Firestore:', error);
      }
    }
    
    // Notify parent component for new orders
    if (onFilesUpdate && lineData) {
      notifyParentWithFiles({
        production: updatedFiles
      });
    }
  };

  const persistProductionFiles = async (updatedFiles) => {
    if (lineId) {
      try {
        const lineRef = doc(db, 'orderLines', lineId);
        const persistableFiles = serializeProductionFilesForFirestore(updatedFiles);
        await updateDoc(lineRef, {
          'files.production': persistableFiles,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('❌ Error persisting production files:', error);
      }
    }

    if (onFilesUpdate && lineData) {
      notifyParentWithFiles({
        production: updatedFiles
      });
    }
  };

  const handleSendToPrint = async (file, index) => {
    if (!file?.url) return;
    setSendingToPrintKey(`${index}:${file.name}`);
    try {
      const copyToHotFolder = httpsCallable(functions, 'copyToHotFolder');
      const result = await copyToHotFolder({
        storageUrl: file.url,
        fileName: file.name
      });

      const updatedFiles = [...productionFiles];
      updatedFiles[index] = {
        ...updatedFiles[index],
        sentToPrintAt: new Date().toISOString(),
        driveFileId: result?.data?.id || null,
        driveWebViewLink: result?.data?.webViewLink || null
      };
      setProductionFiles(updatedFiles);
      await persistProductionFiles(updatedFiles);
    } catch (error) {
      console.error('Error sending file to print hot folder:', error);
      alert(`Failed to send to print: ${error.message}`);
    } finally {
      setSendingToPrintKey(null);
    }
  };

  // Handle file deletion
  const handleDeleteFile = async (file, isMockup = false) => {
    if (!window.confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }

    try {
      if (isMockup) {
        // Update local state
        setMockupFile(null);
        setMockupPreviewUrl(null);
        
        // Delete from Firebase Storage AND Firestore for existing orders
        if (lineId && orderNumber && designName) {
          // Try to delete from Storage (try all possible extensions)
          const sanitized = sanitizeFolderName(designName);
          const extensions = ['png', 'jpg', 'jpeg', 'gif', 'svg'];
          for (const ext of extensions) {
            try {
              const mockupRef = ref(storage, `artwork/${orderNumber}/${sanitized}/Mockups/mockup.${ext}`);
              await deleteObject(mockupRef);
              console.log(`Deleted mockup.${ext} from Storage`);
              break; // If we found and deleted one, stop trying
            } catch (e) {
              // File might not exist with this extension, try next
            }
          }
          
          // Update Firestore
          const lineRef = doc(db, 'orderLines', lineId);
          await updateDoc(lineRef, {
            'files.mockup': null,
            designFileUrl: null,  // Clear legacy field too
            updatedAt: serverTimestamp()
          });
          console.log('Deleted mockup from Firestore');
        }
      } else {
        // Delete from Firebase Storage if it's an existing order
        if (file.storagePath) {
          await deleteObject(ref(storage, file.storagePath));
        } else if (file.ref) {
          // Legacy in-memory object from older loader path
          await deleteObject(file.ref);
        }
        setProductionFiles(prev => prev.filter(f => f.name !== file.name || f.type !== file.type));
        
        // Update Firestore for existing orders
        if (lineId) {
          const lineRef = doc(db, 'orderLines', lineId);
          const lineDoc = await getDoc(lineRef);
          const currentProduction = lineDoc.data()?.files?.production || [];
          const updatedProduction = currentProduction.filter(f => 
            !(f.name === file.name && f.type === file.type)
          );
          await updateDoc(lineRef, {
            'files.production': serializeProductionFilesForFirestore(updatedProduction),
            updatedAt: serverTimestamp()
          });
          console.log('Deleted production file from Firestore');
        }
      }
      
      // Notify parent component for new orders
      if (onFilesUpdate && lineData) {
        if (isMockup) {
          notifyParentWithFiles({
            mockup: null
          });
        } else {
          const updatedProductionLocal = productionFiles.filter(f => f.name !== file.name || f.type !== file.type);
          notifyParentWithFiles({
            production: updatedProductionLocal
          });
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file: ' + error.message);
    }
  };

  const handleSelectLibraryMockup = async (entry) => {
    if (!entry?.mockupUrl) return;

    const applied = applyLibraryMockupToLine(lineData || {}, entry);
    const mockup = applied.files?.mockup;

    setMockupFile(null);
    setMockupPreviewUrl(entry.mockupUrl);

    if (lineId) {
      try {
        await updateDoc(doc(db, 'orderLines', lineId), {
          'files.mockup': {
            url: entry.mockupUrl,
            fileName: entry.mockupFileName || null
          },
          designFileUrl: entry.mockupUrl,
          ...(applied.design && !designName ? { design: applied.design } : {}),
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error attaching library mockup:', error);
        alert('Failed to attach mockup: ' + error.message);
        return;
      }
    }

    if (onFilesUpdate) {
      onFilesUpdate(applied);
    } else {
      notifyParentWithFiles({
        mockup,
        extra: {
          designFileUrl: entry.mockupUrl,
          ...(applied.design ? { design: applied.design } : {})
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        
        <ModalHeader>
          <h3>Files {designName ? `for ${designName}` : ''}</h3>
          {orderNumber && (
            <p>Invoice: {orderNumber}</p>
          )}
        </ModalHeader>

        <GridContainer>
          {/* Row 1, Column 1: Mockup Upload */}
          <Section style={{ gridColumn: 1, gridRow: 1 }}>
            <SectionTitle>Mockup</SectionTitle>
            <UploadArea
              uploading={mockupUploading}
              hasPreview={!!mockupPreviewUrl}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) handleMockupUpload(file);
              }}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => mockupInputRef.current?.click()}
            >
              <input
                ref={mockupInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleMockupUpload(file);
                }}
              />
              {mockupPreviewUrl && (
                <RemoveButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFile({ name: 'mockup' }, true);
                  }}
                  title="Remove mockup"
                >
                  ×
                </RemoveButton>
              )}
              {mockupUploading ? (
                <p>Uploading...</p>
              ) : mockupPreviewUrl ? (
                <img src={mockupPreviewUrl} alt="Mockup preview" />
              ) : (
                <>
                  <p>Drop or click to upload</p>
                  <UploadButton>
                    <FaFolderOpen />
                    <span>Select</span>
                  </UploadButton>
                  {customerId && (
                    <LibraryButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMockupLibrary(true);
                      }}
                    >
                      Browse library
                    </LibraryButton>
                  )}
                </>
              )}
            </UploadArea>
            {customerId && mockupPreviewUrl && (
              <LibraryButton
                type="button"
                onClick={() => setShowMockupLibrary(true)}
                style={{ alignSelf: 'flex-start' }}
              >
                Browse library
              </LibraryButton>
            )}
          </Section>

          {/* Column 2: Production Files (Upload + List) */}
          <Section style={{ gridColumn: 2, gridRow: 1 }}>
            <SectionTitle>Production Files ({productionFiles.length})</SectionTitle>
            <UploadArea
              uploading={productionUploading}
              hasList={productionFiles.length > 0}
              onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files);
                handleProductionUpload(files);
              }}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => productionInputRef.current?.click()}
            >
              <input
                ref={productionInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  handleProductionUpload(files);
                }}
              />
              {productionUploading ? (
                <p>Uploading...</p>
              ) : (
                <>
                  <p>Drop or click to upload</p>
                  <UploadButton>
                    <FaFolderOpen />
                    <span>Select</span>
                  </UploadButton>
                </>
              )}
            </UploadArea>
            
            <FilesList>
              {loading ? (
                <EmptyState>Loading files...</EmptyState>
              ) : productionFiles.length === 0 ? (
                <EmptyState>No production files uploaded yet</EmptyState>
              ) : (
                productionFiles.map((file, index) => (
                  <FileItem key={index}>
                    <FileIcon>{getFileIcon(file.name)}</FileIcon>
                    <FileNameContainer>
                      <FileName title={file.name}>{file.name}</FileName>
                      <FileTypeSelector
                        value={file.type || 'unassigned'}
                        onChange={(e) => handleFileTypeChange(index, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {FILE_TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FileTypeSelector>
                    </FileNameContainer>
                    <FileActions>
                      <FileActionButton
                        onClick={() => handleSendToPrint(file, index)}
                        title={file.sentToPrintAt ? 'Re-send to print' : 'Send to print'}
                        style={{
                          background: file.sentToPrintAt ? 'rgba(var(--al-primary-rgb), 0.25)' : 'rgba(var(--al-primary-rgb), 0.15)',
                          minWidth: 34
                        }}
                        disabled={sendingToPrintKey === `${index}:${file.name}`}
                      >
                        <FaPaperPlane />
                      </FileActionButton>
                      {isPreviewable(file.name) && (
                        <FileActionButton
                          onClick={() => setPreviewFile(file)}
                          title="Preview"
                        >
                          <FaFolderOpen />
                        </FileActionButton>
                      )}
                      <FileActionButton
                        onClick={() => window.open(file.url, '_blank')}
                        title="Download"
                      >
                        <FaDownload />
                      </FileActionButton>
                      <FileActionButton
                        onClick={() => handleDeleteFile(file)}
                        title="Delete"
                        danger
                      >
                        <FaTrash />
                      </FileActionButton>
                    </FileActions>
                  </FileItem>
                ))
              )}
            </FilesList>
          </Section>
        </GridContainer>

        {/* File Preview Modal */}
        {previewFile && (
          <ModalOverlay onClick={() => setPreviewFile(null)}>
            <ModalContent 
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '90vh', width: '900px', height: '700px', padding: 0 }}
            >
              <CloseButton onClick={() => setPreviewFile(null)}>
                <FaTimes />
              </CloseButton>
              <div style={{ padding: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <h3 style={{ margin: 0 }}>{previewFile.name}</h3>
              </div>
              <div style={{ 
                flex: 1, 
                padding: '20px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.3)',
                overflow: 'auto'
              }}>
                {isPreviewable(previewFile.name) && (
                  <img 
                    src={previewFile.url} 
                    alt={previewFile.name}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                )}
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </ModalContent>
      <CustomerMockupsModal
        isOpen={showMockupLibrary}
        onClose={() => setShowMockupLibrary(false)}
        customerId={customerId}
        hasExistingMockup={!!(mockupPreviewUrl || lineData?.files?.mockup?.url || lineData?.designFileUrl)}
        onSelect={handleSelectLibraryMockup}
      />
    </ModalOverlay>
  );
};

export default FilesModal;

